import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { scrollToId } from "../data/site";
import { HeroStats } from "./HeroStats";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span className="block" initial={{ y: "115%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.span>
  </span>
);

const Dot = ({ char = ".", space = false }) => (
  <span className={`inline-block text-[0.5em] align-baseline ${space ? "ml-[0.35em]" : "ml-[0.06em]"}`}>{char}</span>
);

const Backdrop = ({ bgY }) => (
  <>
    <motion.div style={{ y: bgY }} className="absolute inset-0">
      <img src="/images/hero-bg.webp" alt="VIC WRAP STUDIO Melbourne — installer applying carbon fibre vinyl wrap" className="w-full h-[120%] object-cover" />
    </motion.div>
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/45 to-[#0A0A0A]/30" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/85 via-[#0A0A0A]/30 to-transparent" />
    <div className="absolute -left-44 top-1/3 w-[480px] h-[480px] rounded-full bg-[#FF2A2A]/20 blur-[140px]" />
    <div className="absolute right-0 bottom-0 w-[380px] h-[380px] rounded-full bg-[#FF6B00]/15 blur-[120px]" />
    <div className="noise-overlay" />
  </>
);

const Headline = () => (
  <h1 data-testid="hero-headline" className="font-display font-black uppercase leading-[0.88] tracking-tight" style={{ fontSize: "clamp(3.4rem, 10.5vw, 8.75rem)" }}>
    <MaskedLine delay={0.3}><span className="text-white">CONCEPT<Dot char="," /></span></MaskedLine>
    <MaskedLine delay={0.42}><span className="text-gradient">DESIGN<Dot char="," /></span></MaskedLine>
    <MaskedLine delay={0.54}>INSTALL<Dot char="." space /></MaskedLine>
  </h1>
);

const fadeUp = (delay) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay } });

export default function Hero() {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 550], [1, 0.25]);

  return (
    <section id="home" className="relative min-h-screen flex items-end overflow-hidden">
      <Backdrop bgY={bgY} />

      <motion.div style={{ opacity: fade }} className="relative container-x w-full pt-44 pb-20 sm:pb-24">
        <motion.div {...fadeUp(0.15)} className="inline-flex items-center gap-2 rounded-full border border-[#FF6B00]/40 bg-[#FF6B00]/10 px-4 py-1.5 mb-7">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00] animate-pulse" />
          <span className="font-tech text-[11px] uppercase tracking-[0.25em] text-[#FFB37A]">Melbourne · Design & Installation Studio</span>
        </motion.div>

        <Headline />

        <motion.p data-testid="hero-subheadline" {...fadeUp(0.75)} className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed">
          Graphic Design, Vehicle Wrapping, Signage & Professional Window Film Installation in Melbourne.
        </motion.p>

        <motion.div {...fadeUp(0.9)} className="mt-9 flex flex-wrap gap-4">
          <button data-testid="hero-cta-quote-button" onClick={() => scrollToId("quote")} className="btn-primary">
            GET A FREE QUOTE <ArrowRight size={16} />
          </button>
          <button data-testid="hero-cta-services-button" onClick={() => scrollToId("services")} className="btn-outline">
            VIEW OUR SERVICES
          </button>
        </motion.div>

        <HeroStats />
      </motion.div>

      <motion.button onClick={() => scrollToId("services")} animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }} className="absolute bottom-6 right-6 text-zinc-500 hover:text-[#FF6B00] transition-colors hidden sm:block" aria-label="Scroll to services">
        <ChevronDown size={28} />
      </motion.button>
    </section>
  );
}
